import { useState, useEffect, useMemo } from "react";
import type { MaintenanceWindow, MaintenanceAffectedService, MaintenanceResponse } from "../../types/incidents";
import type { ServiceRegionOption } from "../../types/monitoring";
import { adminFetch } from "../../lib/api";

function fmtDate(s: string) { return new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }

function windowStatus(w: MaintenanceWindow): string {
  const now = Date.now();
  const start = new Date(w.starts_at).getTime();
  const end = new Date(w.ends_at).getTime();
  if (now >= start && now <= end) return "active";
  if (now < start) return "upcoming";
  return "ended";
}

/** Group flat service-region list into service -> component -> regions hierarchy */
interface RegionNode { id: string; label: string; region_name: string; }
interface ComponentNode { id: string | null; name: string; regions: RegionNode[]; }
interface ServiceNode { name: string; components: ComponentNode[]; }

function buildHierarchy(items: ServiceRegionOption[]): ServiceNode[] {
  const serviceMap = new Map<string, ServiceNode>();
  for (const item of items) {
    if (!serviceMap.has(item.service_name)) {
      serviceMap.set(item.service_name, { name: item.service_name, components: [] });
    }
    const svc = serviceMap.get(item.service_name)!;
    const compKey = item.component_id ?? "__direct__";
    let comp = svc.components.find(c => (c.id ?? "__direct__") === compKey);
    if (!comp) {
      comp = { id: item.component_id, name: item.component_name ?? item.service_name, regions: [] };
      svc.components.push(comp);
    }
    comp.regions.push({ id: item.id, label: item.region_name, region_name: item.region_name });
  }
  return [...serviceMap.values()];
}

function AffectedServicesList({ services }: { services: MaintenanceAffectedService[] }) {
  if (services.length === 0) return <span style={{ color: "var(--aptean-text-muted)", fontSize: "0.75rem" }}>None</span>;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem" }}>
      {services.map(s => (
        <span key={s.service_region_id} style={{
          fontSize: "0.7rem", padding: "0.1rem 0.5rem",
          background: "var(--aptean-bg-overlay)", borderRadius: "3px",
          color: "var(--aptean-text-secondary)", border: "1px solid var(--aptean-border)",
        }}>
          {s.component_name ? `${s.component_name} / ${s.region_name}` : `${s.service_name} / ${s.region_name}`}
        </span>
      ))}
    </div>
  );
}

/** Hierarchical checkbox selector for service regions */
function ServiceRegionSelector({
  hierarchy,
  selected,
  onChange,
}: {
  hierarchy: ServiceNode[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(next);
  }

  function toggleAll(ids: string[]) {
    const allSelected = ids.every(id => selected.has(id));
    const next = new Set(selected);
    if (allSelected) ids.forEach(id => next.delete(id));
    else ids.forEach(id => next.add(id));
    onChange(next);
  }

  function toggleExpand(key: string) {
    const next = new Set(expanded);
    if (next.has(key)) next.delete(key); else next.add(key);
    setExpanded(next);
  }

  return (
    <div style={{ border: "1px solid var(--aptean-border)", borderRadius: "6px", maxHeight: "280px", overflowY: "auto", background: "var(--aptean-bg-surface)" }}>
      {hierarchy.length === 0 && (
        <div style={{ padding: "1rem", textAlign: "center", color: "var(--aptean-text-muted)", fontSize: "0.875rem" }}>
          No services configured
        </div>
      )}
      {hierarchy.map(svc => {
        const allSvcIds = svc.components.flatMap(c => c.regions.map(r => r.id));
        const svcAllSelected = allSvcIds.length > 0 && allSvcIds.every(id => selected.has(id));
        const svcSomeSelected = allSvcIds.some(id => selected.has(id));
        const svcKey = `svc-${svc.name}`;
        const svcExpanded = expanded.has(svcKey);

        // Determine if service has components (real component structure) or just direct regions
        const hasComponents = svc.components.some(c => c.id !== null);

        return (
          <div key={svc.name} style={{ borderBottom: "1px solid var(--aptean-border)" }}>
            {/* Service header row */}
            <div style={{
              display: "flex", alignItems: "center", gap: "0.5rem",
              padding: "0.5rem 0.75rem", background: "var(--aptean-bg-overlay)",
              cursor: "pointer",
            }}>
              <input
                type="checkbox"
                checked={svcAllSelected}
                ref={el => { if (el) el.indeterminate = !svcAllSelected && svcSomeSelected; }}
                onChange={() => toggleAll(allSvcIds)}
                style={{ cursor: "pointer" }}
              />
              <span
                style={{ flex: 1, fontWeight: 600, fontSize: "0.875rem", color: "var(--aptean-text-primary)", userSelect: "none" }}
                onClick={() => toggleExpand(svcKey)}
              >
                {svc.name}
                <span style={{ marginLeft: "0.35rem", fontSize: "0.7rem", color: "var(--aptean-text-muted)" }}>
                  {svcSomeSelected ? `(${allSvcIds.filter(id => selected.has(id)).length}/${allSvcIds.length})` : `(${allSvcIds.length})`}
                </span>
              </span>
              <button
                type="button"
                onClick={() => toggleExpand(svcKey)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--aptean-text-muted)", fontSize: "0.75rem", padding: "0" }}
              >
                {svcExpanded ? "▲" : "▼"}
              </button>
            </div>

            {svcExpanded && (
              <div>
                {svc.components.map(comp => {
                  const compIds = comp.regions.map(r => r.id);
                  const compAllSelected = compIds.length > 0 && compIds.every(id => selected.has(id));
                  const compSomeSelected = compIds.some(id => selected.has(id));
                  const compKey = `comp-${svc.name}-${comp.id ?? "direct"}`;
                  const compExpanded = expanded.has(compKey);

                  return (
                    <div key={compKey}>
                      {/* Component row (only show if there are real components) */}
                      {hasComponents && (
                        <div style={{
                          display: "flex", alignItems: "center", gap: "0.5rem",
                          padding: "0.4rem 0.75rem 0.4rem 1.75rem",
                          borderTop: "1px solid var(--aptean-border)",
                          cursor: "pointer",
                        }}>
                          <input
                            type="checkbox"
                            checked={compAllSelected}
                            ref={el => { if (el) el.indeterminate = !compAllSelected && compSomeSelected; }}
                            onChange={() => toggleAll(compIds)}
                            style={{ cursor: "pointer" }}
                          />
                          <span
                            style={{ flex: 1, fontSize: "0.8125rem", color: "var(--aptean-text-secondary)", userSelect: "none" }}
                            onClick={() => toggleExpand(compKey)}
                          >
                            {comp.name}
                            <span style={{ marginLeft: "0.35rem", fontSize: "0.7rem", color: "var(--aptean-text-muted)" }}>
                              {compSomeSelected ? `(${compIds.filter(id => selected.has(id)).length}/${compIds.length})` : `(${compIds.length})`}
                            </span>
                          </span>
                          <button
                            type="button"
                            onClick={() => toggleExpand(compKey)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--aptean-text-muted)", fontSize: "0.7rem", padding: "0" }}
                          >
                            {compExpanded ? "▲" : "▼"}
                          </button>
                        </div>
                      )}

                      {/* Region rows */}
                      {(!hasComponents || compExpanded) && comp.regions.map(region => (
                        <label
                          key={region.id}
                          style={{
                            display: "flex", alignItems: "center", gap: "0.5rem",
                            padding: hasComponents ? "0.35rem 0.75rem 0.35rem 3rem" : "0.35rem 0.75rem 0.35rem 1.75rem",
                            borderTop: "1px solid var(--aptean-border)",
                            cursor: "pointer",
                            background: selected.has(region.id) ? "color-mix(in srgb, var(--aptean-teal) 8%, transparent)" : "transparent",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selected.has(region.id)}
                            onChange={() => toggle(region.id)}
                            style={{ cursor: "pointer" }}
                          />
                          <span style={{ fontSize: "0.8125rem", color: "var(--aptean-text-primary)" }}>{region.region_name}</span>
                        </label>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Convert ISO string to datetime-local input value (YYYY-MM-DDTHH:MM) */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

type RecurrenceType = "none" | "weekdays" | "weekly";

const RECURRENCE_LABELS: Record<RecurrenceType, string> = {
  none: "One-time",
  weekdays: "Weekdays (Mon–Fri)",
  weekly: "Weekly (same day)",
};

export function MaintenancePage() {
  const [data, setData] = useState<MaintenanceResponse | null>(null);
  const [serviceRegions, setServiceRegions] = useState<ServiceRegionOption[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<MaintenanceWindow | null>(null);
  const [fTitle, setFTitle] = useState("");
  const [fDesc, setFDesc] = useState("");
  const [fStart, setFStart] = useState("");
  const [fEnd, setFEnd] = useState("");
  const [fRecurrence, setFRecurrence] = useState<RecurrenceType>("none");
  const [fRecurrenceEnd, setFRecurrenceEnd] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [fError, setFError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function load() {
    const r = await adminFetch("/api/v1/admin/maintenance/");
    if (r.ok) setData(await r.json());
  }

  async function loadServiceRegions() {
    const r = await adminFetch("/api/v1/admin/service-regions/");
    if (r.ok) setServiceRegions(await r.json());
  }

  useEffect(() => { load(); loadServiceRegions(); }, []);

  const hierarchy = useMemo(() => buildHierarchy(serviceRegions), [serviceRegions]);

  function openCreate() {
    setFTitle(""); setFDesc(""); setFStart(""); setFEnd("");
    setFRecurrence("none"); setFRecurrenceEnd("");
    setSelectedIds(new Set()); setFError("");
    setEditTarget(null);
    setShowCreate(true);
  }

  function openEdit(w: MaintenanceWindow) {
    setFTitle(w.title);
    setFDesc(w.description ?? "");
    setFStart(toLocalInput(w.starts_at));
    setFEnd(toLocalInput(w.ends_at));
    setFRecurrence(w.recurrence ?? "none");
    setFRecurrenceEnd(w.recurrence_end_date ?? "");
    setSelectedIds(new Set(w.affected_services.map(s => s.service_region_id)));
    setFError("");
    setEditTarget(w);
    setShowCreate(true);
  }

  async function submitForm(e: React.FormEvent) {
    e.preventDefault();
    setFError("");
    if (fEnd <= fStart) { setFError("End time must be after start time."); return; }
    if (fRecurrence !== "none" && !fRecurrenceEnd) {
      setFError("Please set the 'Repeat until' date for recurring windows.");
      return;
    }
    setSubmitting(true);
    try {
      let r: Response;
      if (editTarget) {
        r = await adminFetch(`/api/v1/admin/maintenance/${editTarget.id}/`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: fTitle,
            description: fDesc,
            starts_at: new Date(fStart).toISOString(),
            ends_at: new Date(fEnd).toISOString(),
          }),
        });
      } else {
        r = await adminFetch("/api/v1/admin/maintenance/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: fTitle,
            description: fDesc,
            starts_at: new Date(fStart).toISOString(),
            ends_at: new Date(fEnd).toISOString(),
            service_region_ids: [...selectedIds],
            recurrence: fRecurrence,
            recurrence_end_date: fRecurrence !== "none" ? fRecurrenceEnd : null,
          }),
        });
      }
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || body.non_field_errors?.[0] || `HTTP ${r.status}`);
      }
      setShowCreate(false);
      setEditTarget(null);
      await load();
    } catch (err) {
      setFError(err instanceof Error ? err.message : editTarget ? "Failed to update maintenance window." : "Failed to schedule maintenance.");
    } finally { setSubmitting(false); }
  }

  async function endWindow(id: string, title: string) {
    if (!confirm(`End maintenance window "${title}" now?\n\nThis will set the end time to right now and immediately restore normal status.`)) return;
    const r = await adminFetch(`/api/v1/admin/maintenance/${id}/end/`, { method: "POST" });
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      alert(body.detail || `Failed to end maintenance window (HTTP ${r.status})`);
      return;
    }
    await load();
  }

  async function deleteWindow(w: MaintenanceWindow) {
    const hasSeries = !!w.recurrence_series_id;
    let url = `/api/v1/admin/maintenance/${w.id}/`;
    if (hasSeries) {
      const choice = confirm(
        `"${w.title}" is part of a recurring series.\n\n` +
        `OK = Cancel ALL upcoming occurrences in this series\n` +
        `Cancel = Cancel only this occurrence`
      );
      if (choice === null) return; // user closed dialog — treat as abort
      if (choice) url += "?series=true";
    } else {
      if (!confirm(`Delete maintenance window "${w.title}"?`)) return;
    }
    await adminFetch(url, { method: "DELETE" });
    await load();
  }

  const all: MaintenanceWindow[] = [...(data?.active ?? []), ...(data?.upcoming ?? []), ...(data?.past ?? [])];
  const selectedCount = selectedIds.size;

  return (
    <div className="ac-page">
      <div className="ac-page-header">
        <div className="ac-page-header__left">
          <h1 className="ac-page-title">Maintenance Windows</h1>
          <p className="ac-page-subtitle">Schedule and manage planned maintenance periods</p>
        </div>
        <div className="ac-page-header__actions">
          <button type="button" className="aptean-btn aptean-btn--secondary" onClick={load}>Refresh</button>
          <button type="button" className="aptean-btn aptean-btn--primary" onClick={openCreate}>
            + Schedule Maintenance
          </button>
        </div>
      </div>

      <div className="ac-table-wrap">
        <table className="ac-table">
          <thead>
            <tr>
              <th style={{ width: "26%" }}>Title</th>
              <th>Status</th>
              <th>Recurrence</th>
              <th>Starts</th>
              <th>Ends</th>
              <th>Duration</th>
              <th>Affected</th>
              <th className="ac-th--right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {all.length === 0 ? (
              <tr><td colSpan={7}>
                <div className="ac-empty">
                  <div className="ac-empty__icon">&#128295;</div>
                  <div className="ac-empty__title">No maintenance windows</div>
                  <div className="ac-empty__body">No active or upcoming maintenance scheduled.</div>
                </div>
              </td></tr>
            ) : all.map(w => {
              const st = windowStatus(w);
              const dur = Math.round((new Date(w.ends_at).getTime() - new Date(w.starts_at).getTime()) / 60000);
              const durLabel = dur >= 60 ? `${Math.floor(dur / 60)}h ${dur % 60}m` : `${dur}m`;
              return (
                <tr key={w.id}>
                  <td>
                    <div className="ac-row-title">{w.title}</div>
                    {w.description && <div className="ac-row-sub">{w.description}</div>}
                  </td>
                  <td><span className={`ac-badge ac-badge--${st === "active" ? "active" : st === "upcoming" ? "info" : "neutral"}`}>{st}</span></td>
                  <td>
                    {w.recurrence && w.recurrence !== "none" ? (
                      <span title={`Repeats ${RECURRENCE_LABELS[w.recurrence as RecurrenceType] ?? w.recurrence}${w.recurrence_end_date ? ` until ${w.recurrence_end_date}` : ""}`}
                        style={{ fontSize: "0.75rem", padding: "0.1rem 0.5rem", borderRadius: "3px",
                          background: "color-mix(in srgb, var(--aptean-teal) 15%, transparent)",
                          color: "var(--aptean-teal)", border: "1px solid var(--aptean-teal)", whiteSpace: "nowrap" }}>
                        ↻ {w.recurrence === "weekdays" ? "Weekdays" : "Weekly"}
                      </span>
                    ) : (
                      <span style={{ fontSize: "0.75rem", color: "var(--aptean-text-muted)" }}>One-time</span>
                    )}
                  </td>
                  <td className="ac-td--muted">{fmtDate(w.starts_at)}</td>
                  <td className="ac-td--muted">{fmtDate(w.ends_at)}</td>
                  <td className="ac-td--muted">{durLabel}</td>
                  <td><AffectedServicesList services={w.affected_services ?? []} /></td>
                  <td className="ac-td--actions ac-td--right" style={{ whiteSpace: "nowrap" }}>
                    <div style={{ display: "flex", gap: "0.375rem", justifyContent: "flex-end", alignItems: "center" }}>
                      {st === "active" && (
                        <button type="button" className="aptean-btn"
                          style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem", background: "var(--aptean-amber, #c47f17)", color: "#fff", border: "none" }}
                          title="End this maintenance window immediately"
                          onClick={() => endWindow(w.id, w.title)}>
                          End Now
                        </button>
                      )}
                      {st === "upcoming" && (
                        <button type="button" className="aptean-btn aptean-btn--ghost"
                          style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }}
                          onClick={() => openEdit(w)}>
                          Edit
                        </button>
                      )}
                      <button type="button" className="aptean-btn aptean-btn--danger"
                          style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }}
                          onClick={() => deleteWindow(w)}>
                          {st !== "ended" && w.recurrence_series_id ? "Cancel" : "Delete"}
                        </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <div className="ac-overlay" onClick={e => e.target === e.currentTarget && (setShowCreate(false), setEditTarget(null))}>
          <div className="ac-dialog" style={{ maxWidth: "560px", width: "100%" }}>
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">{editTarget ? "Edit Maintenance Window" : "Schedule Maintenance"}</h2>
              <button type="button" className="ac-dialog__close" onClick={() => { setShowCreate(false); setEditTarget(null); }}>&#10005;</button>
            </div>
            <form onSubmit={submitForm}>
              <div className="ac-dialog__body">
                {fError && <div className="ac-alert ac-alert--error">{fError}</div>}
                <div className="ac-field">
                  <label htmlFor="m-title">Title *</label>
                  <input id="m-title" type="text" className="aptean-input" value={fTitle} onChange={e => setFTitle(e.target.value)} required />
                </div>
                <div className="ac-field">
                  <label htmlFor="m-desc">Description</label>
                  <textarea id="m-desc" className="aptean-input" rows={2} value={fDesc} onChange={e => setFDesc(e.target.value)} />
                </div>
                <div className="ac-field-row">
                  <div className="ac-field">
                    <label htmlFor="m-start">Starts at *</label>
                    <input id="m-start" type="datetime-local" className="aptean-input" value={fStart} onChange={e => setFStart(e.target.value)} required />
                  </div>
                  <div className="ac-field">
                    <label htmlFor="m-end">Ends at *</label>
                    <input id="m-end" type="datetime-local" className="aptean-input" value={fEnd} onChange={e => setFEnd(e.target.value)} required />
                  </div>
                </div>
                {!editTarget && (
                  <div className="ac-field">
                    <label>Recurrence</label>
                    <div style={{ display: "flex", gap: "1rem", marginTop: "0.35rem", flexWrap: "wrap" }}>
                      {(["none", "weekdays", "weekly"] as RecurrenceType[]).map(opt => (
                        <label key={opt} style={{ display: "flex", alignItems: "center", gap: "0.35rem", cursor: "pointer", fontSize: "0.875rem" }}>
                          <input
                            type="radio"
                            name="recurrence"
                            value={opt}
                            checked={fRecurrence === opt}
                            onChange={() => { setFRecurrence(opt); if (opt === "none") setFRecurrenceEnd(""); }}
                          />
                          {RECURRENCE_LABELS[opt]}
                        </label>
                      ))}
                    </div>
                    {fRecurrence !== "none" && (
                      <div style={{ marginTop: "0.6rem" }}>
                        <label htmlFor="m-rec-end" style={{ fontSize: "0.8125rem", color: "var(--aptean-text-secondary)" }}>
                          Repeat until *
                        </label>
                        <input
                          id="m-rec-end"
                          type="date"
                          className="aptean-input"
                          style={{ marginTop: "0.25rem" }}
                          value={fRecurrenceEnd}
                          onChange={e => setFRecurrenceEnd(e.target.value)}
                          required
                          min={fStart ? fStart.split("T")[0] : undefined}
                        />
                        <div style={{ marginTop: "0.3rem", fontSize: "0.75rem", color: "var(--aptean-text-muted)" }}>
                          {fRecurrence === "weekdays"
                            ? "A window will be created for every weekday (Mon–Fri) from the start date through this date."
                            : "A window will be created every week on the same day through this date."}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {editTarget && editTarget.recurrence !== "none" && (
                  <div className="ac-field">
                    <div style={{ padding: "0.5rem 0.75rem", background: "color-mix(in srgb, var(--aptean-teal) 8%, transparent)",
                      borderRadius: "6px", border: "1px solid var(--aptean-teal)", fontSize: "0.8125rem" }}>
                      <strong>Recurring series</strong> — this occurrence repeats&nbsp;
                      {RECURRENCE_LABELS[editTarget.recurrence as RecurrenceType] ?? editTarget.recurrence}
                      {editTarget.recurrence_end_date ? ` until ${editTarget.recurrence_end_date}` : ""}.
                      Editing here changes only this occurrence.
                    </div>
                  </div>
                )}
                {!editTarget && (
                  <div className="ac-field">
                    <label>
                      Affected Services / Components / Regions
                      {selectedCount > 0 && (
                        <span style={{ marginLeft: "0.5rem", fontSize: "0.75rem", color: "var(--aptean-teal)", fontWeight: 600 }}>
                          {selectedCount} selected
                        </span>
                      )}
                    </label>
                    <div style={{ marginTop: "0.25rem", fontSize: "0.75rem", color: "var(--aptean-text-muted)", marginBottom: "0.4rem" }}>
                      Expand a service to select individual regions. Use the service/component checkbox to select all at once.
                    </div>
                    <ServiceRegionSelector
                      hierarchy={hierarchy}
                      selected={selectedIds}
                      onChange={setSelectedIds}
                    />
                    {selectedCount > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedIds(new Set())}
                        style={{ marginTop: "0.4rem", background: "none", border: "none", cursor: "pointer", fontSize: "0.75rem", color: "var(--aptean-text-muted)", padding: "0" }}
                      >
                        Clear selection
                      </button>
                    )}
                  </div>
                )}
                {editTarget && editTarget.affected_services.length > 0 && (
                  <div className="ac-field">
                    <label>Affected Services</label>
                    <div style={{ marginTop: "0.25rem" }}>
                      <AffectedServicesList services={editTarget.affected_services} />
                    </div>
                    <div style={{ marginTop: "0.375rem", fontSize: "0.75rem", color: "var(--aptean-text-muted)" }}>
                      To change affected services, delete this window and create a new one.
                    </div>
                  </div>
                )}
              </div>
              <div className="ac-dialog__footer">
                <button type="button" className="aptean-btn aptean-btn--ghost" onClick={() => { setShowCreate(false); setEditTarget(null); }}>Cancel</button>
                <button type="submit" className="aptean-btn aptean-btn--primary" disabled={submitting}>
                  {submitting ? (editTarget ? "Saving..." : "Scheduling...") : (editTarget ? "Save Changes" : "Schedule")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
